/**
 * The wafer — the thing that travels.
 *
 * Until now every module was an island. Each one owned a private thickness
 * array, no simulation imported another, and the takes/makes chain written on
 * each tool's scope page was documentation rather than data. A fab was
 * modelled as twenty independent trainers that happened to be in one building.
 *
 * TWO KINDS OF COUPLING, AND THIS IS THE SECOND
 *
 * The house systems already built are LATERAL: one shared service reaching many
 * tools at once, asking "is it this tool or the building?". This file is the
 * SERIAL kind: tool N's output is tool N+1's input, asking "is it this
 * step or an earlier one?". That second question is most of process
 * integration, and it is the one the curriculum's claims about consequential
 * error and diagnostic transfer actually rest on - a mistake is only
 * consequential if it can travel.
 *
 * WHAT A WAFER IS HERE
 *
 * A radial grid, a film stack built bottom-up, and the running accounts that
 * every downstream tool draws on. Deliberately not a full device model: it
 * carries what one tool has to tell another, and nothing else. Fields are
 * added when a real edge needs them rather than in anticipation.
 *
 * Thickness is per-radius, not a scalar, because uniformity is half of what
 * every process tool is judged on and a scalar would throw it away at the
 * first handoff.
 */

import { t, tOr } from '../../i18n/i18n'

/** Materials a layer can be made of. Indices live in the metrology module. */
export type FilmMaterial =
  | 'sio2'
  | 'al2o3'
  | 'si3n4'
  | 'polysilicon'
  | 'photoresist'
  | 'copper'
  // Single-crystal silicon, grown epitaxially. Distinct from polysilicon
  // because everything about it is distinct: it continues the substrate's
  // lattice, it is the layer devices are built in, and a reflectometer sees
  // silicon-on-silicon as no film at all.
  | 'silicon'

export interface FilmLayer {
  material: FilmMaterial
  /** Thickness at each radial sample [nm]. Same grid as the wafer. */
  thicknessNm: Float64Array
  /**
   * Fraction of the layer that is not the ideal material - porous,
   * carbon-contaminated, non-stoichiometric. Zero for a film grown properly.
   *
   * Carried because it is the difference between a film being THIN and a film
   * being WRONG, and those fail differently downstream: a thin film measures
   * correctly and a wrong one does not.
   */
  imperfection: number
  /** Asset tag of the tool that grew it. */
  madeBy: string
  /** One line for the wafer's history, in a technician's words. */
  note: string
}

/**
 * The crystal this lot was cut from.
 *
 * A wafer's substrate is not a default, it is a decision somebody made at the
 * puller: the dopant in the melt sets the background every junction is formed
 * against, and the oxygen sets the gettering and the mechanical strength. This
 * record is how those decisions reach the tools that live with them - and how
 * a resistivity problem three months old shows up at a furnace today.
 */
export interface CrystalPass {
  dopant: 'boron' | 'phosphorus' | 'antimony' | 'arsenic'
  /** Doping at the seed end - the slices this lot represents [cm^-3]. */
  dopingCm3: number
  resistivityOhmCm: number
  /** Interstitial oxygen [cm^-3]. */
  oxygenCm3: number
  /** True when the pull lost dislocation-free structure. */
  structureLost: boolean
  /** Usable ingot length inside the resistivity spec [mm]. */
  usableLengthMm: number
  tool: string
}

/**
 * The epitaxial layer this wafer carries, if it carries one.
 *
 * Recorded separately from the film stack for the same reason the implants
 * are: the tools downstream need numbers, not a prose line. Once this exists,
 * the devices live HERE - the epi doping, not the bulk's, is the background
 * every junction forms against, which is most of why the layer was grown.
 */
export interface EpiPass {
  dopant: 'boron' | 'phosphorus' | 'antimony' | 'arsenic'
  /** Doping of the flat zone [cm^-3]. */
  dopingCm3: number
  thicknessUm: number
  /** Autodoped transition at the seam, smeared from the substrate [um]. */
  transitionUm: number
  tool: string
}

/**
 * One implant this wafer has received.
 *
 * Recorded rather than summarised, because the tool that needs it next - the
 * furnace - needs the actual profile parameters, not a prose line. This is
 * what lets an implant DONE AT IMP-10 be the thing THRM-04 anneals, instead of
 * the furnace panel quietly re-deriving a profile from its own knobs.
 */
export interface ImplantPass {
  /** What went in, as the implanter names it (BF2 stays BF2 here). */
  species: string
  /** What it dopes as, which is what diffusion cares about. */
  dopant: 'boron' | 'phosphorus' | 'arsenic'
  energyKeV: number
  doseCm2: number
  /** As-implanted range and straggle [nm]. */
  rangeNm: number
  straggleNm: number
  tool: string
  /**
   * How many thermal steps the wafer had already seen when this implant went
   * in - the bookmark that keeps the budget honest about ORDER.
   *
   * Heat only moves dopant that is already in the wafer, so EPI-20's hour
   * above 1000 C, which happens before any implant exists, must not be
   * replayed onto a junction implanted afterwards. The furnace slices the
   * thermal history from this bookmark rather than replaying all of it.
   * Absent on records from before the field existed, which reads as 0 -
   * correct for those wafers, whose history could only have come from the
   * furnace after the implant.
   */
  thermalStepsBefore?: number
}

/**
 * One thermal step this wafer has seen.
 *
 * The budget as a RECORD rather than a number, because Dt collapses dopant and
 * temperature into one figure that no solver can un-collapse. Anything that
 * wants to know where a junction ended up can replay these steps exactly;
 * `thermalDt` below stays as the single-number summary for display.
 */
export interface ThermalPass {
  label: string
  tempC: number
  seconds: number
  ambient: 'dry' | 'wet' | 'inert'
  tool: string
}

/**
 * A value in a history line: a number or a name as it stands, or `{ k }` for
 * a WORD that has a translation of its own - a regime, a failure mode, a map
 * signature - looked up under `tool.history.word` when the line is read.
 */
export type StepValue = string | number | { k: string }

/** One clause of a history line: a catalogue key and what goes into it. */
export interface StepPart {
  key: string
  values?: Record<string, StepValue>
}

/**
 * What a tool writes on the travel card: the clauses, and the sentence they
 * made in the language of the moment.
 */
export interface StepNote {
  parts: StepPart[]
  text: string
}

/**
 * The pattern this wafer carries, and how far it has got.
 *
 * Every other account on the wafer is a film or a dose; this is the one that
 * is a SHAPE. It exists because the patterning tools were the last ones with
 * nothing to hand on: LITH-03 and ETCH-01 never wrote to the lot at all, so a
 * travel card could not show an exposure or an etch, and the three tools that
 * send lots to them (PAT-21, SEM-23, CMP-11) were handing them to a dead end.
 * It is also what lets a tool turn away a lot it has no business with - a
 * CD-SEM asked to measure a wafer nothing was printed on, an etcher asked to
 * transfer a pattern that is not there.
 *
 * `stage` is where the pattern lives: in resist after the scanner, still in
 * resist but with a number on it after the CD-SEM, in the film after etch.
 */
export interface PatternPass {
  source: 'duv' | 'euv'
  pitchNm: number
  /** Printed line width [nm], as the exposing tool computed it. */
  cdNm: number
  /** Line-edge roughness, 3 sigma [nm]. */
  lerNm: number
  /** What the CD-SEM reported, once one has looked [nm]. */
  measuredCdNm?: number
  stage: 'resist' | 'measured' | 'etched'
  tool: string
}

export interface WaferStep {
  tool: string
  /**
   * The line as it read when it was written. Kept because the journal and
   * the technicians quote it, and because a lot saved before `parts` existed
   * has nothing else.
   */
  action: string
  /**
   * The same line as data, so the travel card follows the language.
   *
   * A history line used to be an English template string built at the tool,
   * which meant a student working in Spanish read a card that was Spanish
   * down to the first step and English from there on - and a lot carried
   * across a language switch kept whatever it was written in. The clauses
   * are stored and `stepText` renders them at the moment of reading.
   */
  parts?: StepPart[]
}

const wordOf = (v: StepValue): string | number =>
  typeof v === 'object' ? tOr(`tool.history.word.${v.k.replace(/[^A-Za-z0-9]+/g, '_')}`, v.k) : v

const renderPart = (p: StepPart): string => {
  const values: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(p.values ?? {})) values[k] = wordOf(v)
  return t(`tool.history.${p.key}`, values)
}

/** A clause. `false` and `null` are accepted by `stepNote` so a condition can drop one in place. */
export const part = (key: string, values?: Record<string, StepValue>): StepPart => ({ key, values })

/** Clauses into a note. They are joined as written: each carries its own leading comma or dash. */
export function stepNote(...parts: Array<StepPart | false | null | undefined>): StepNote {
  const kept = parts.filter((p): p is StepPart => !!p)
  return { parts: kept, text: kept.map(renderPart).join('') }
}

/** A history line for `tool`, from a note or from plain text. */
export const toStep = (tool: string, note: string | StepNote): WaferStep =>
  typeof note === 'string' ? { tool, action: note } : { tool, action: note.text, parts: note.parts }

/** The line in the language being read NOW; the stored text where there are no clauses. */
export const stepText = (step: WaferStep): string =>
  step.parts && step.parts.length > 0 ? step.parts.map(renderPart).join('') : step.action

/** A note's text, whichever kind it is - for a journal summary or a film's own label. */
export const noteText = (note: string | StepNote): string => (typeof note === 'string' ? note : note.text)

export interface Wafer {
  /** Lot and slot, as it would be written on a travel card. */
  id: string
  /** Normalised radius of each sample: 0 at the centre, 1 at the wafer edge. */
  x: Float64Array
  /** Film stack, bottom-up. The last entry is the surface. */
  stack: FilmLayer[]
  /**
   * Accumulated Dt across every thermal step [cm^2] - the thermal budget
   * account. Written by the furnace, read by anything that cares where a
   * junction ended up.
   */
  thermalDt: number
  /**
   * What shape the lot currently is. An ingot is a lot the same way a diced
   * wafer still is - the travel card starts at the puller, not at the saw -
   * but only WPR-08 can turn it into something the other tools accept.
   * Absent means 'wafer', so every lot from before this field exists behaves.
   */
  form?: 'ingot' | 'wafer'
  /** The crystal this lot was cut from. Absent on a stock blank. */
  crystal?: CrystalPass
  /** The epitaxial device layer, once EPI-20 has grown one. */
  epi?: EpiPass
  /** Every implant received, in order. Absent on a wafer never implanted. */
  implants?: ImplantPass[]
  /** Every thermal step seen, in order. The replayable form of thermalDt. */
  thermalHistory?: ThermalPass[]
  /** Defects picked up so far [per cm^2]. */
  defectDensity: number
  /**
   * Worst-to-best spread of the surface across a die [nm].
   *
   * The focus budget the next lithography layer inherits. Written by whatever
   * last changed the topography - CMP flattens it, deposition and etch add to
   * it - and read by LITH-03, which has nowhere to put it except into defocus.
   *
   * This is the field that makes planarisation matter to anything. Without it,
   * CMP would be a tool that produces a number nobody downstream ever reads.
   */
  surfaceRangeNm: number
  /**
   * Once the wafer has been singulated, what came off it.
   *
   * The travel card does not stop at dicing - the lot number follows the die
   * into assembly and onto a tester, which is why this hangs off the same
   * object rather than becoming a new one. A wafer that has been diced is still
   * that wafer's lot; it is just no longer shaped like a wafer.
   */
  dies?: DiePopulation
  /** And what those die became once they were packaged. */
  packages?: PackageLot
  /** The most recent pattern printed on this wafer, if any. See PatternPass. */
  pattern?: PatternPass
  /** Everything that has happened to it, in order. */
  history: WaferStep[]
}

/**
 * The die population after singulation.
 *
 * Strength is a DISTRIBUTION rather than a number, and carrying it that way is
 * the point. Silicon fails from the worst flaw present, not the average one, so
 * a mean die strength tells you almost nothing about the tail that will
 * actually crack in a mould press three tools later.
 */
export interface DiePopulation {
  /** Good die coming out of singulation. */
  count: number
  /** Final die thickness [um]. */
  thicknessUm: number
  /** Weibull characteristic strength of the ground back side [MPa]. */
  strengthMPa: number
  /** And of the diced edge, which is a separate flaw population. */
  edgeStrengthMPa: number
  /** Weibull modulus. Low means a wide spread and a long weak tail. */
  weibullM: number
  /** Depth of grinding damage left on the back side [um]. */
  damageUm: number
}

/** What the die became once packaged. */
export interface PackageLot {
  count: number
  /** Package warpage at reflow [um across the body]. */
  warpageUm: number
  /** Fraction of packages carrying a bond or mould defect. */
  defectFraction: number
  /** Moisture absorbed, as a fraction of saturation. */
  moistureFraction: number
}

/** Default radial resolution. Matches the process solvers' wafer sampling. */
export const WAFER_SAMPLES = 32

export function bareWafer(id = 'LOT-4417 / 12', samples = WAFER_SAMPLES): Wafer {
  const x = new Float64Array(samples)
  for (let i = 0; i < samples; i++) x[i] = samples === 1 ? 0 : i / (samples - 1)
  return {
    id,
    x,
    stack: [],
    thermalDt: 0,
    defectDensity: 0,
    // A polished bare wafer is flat to well under a nanometre across a die.
    surfaceRangeNm: 0.3,
    history: [toStep('WPR-08', stepNote(part('BareWafer')))],
  }
}

/** Add a layer to the top of the stack and record it in the history. */
export function addLayer(wafer: Wafer, layer: FilmLayer, note?: StepNote): Wafer {
  return {
    ...wafer,
    stack: [...wafer.stack, layer],
    history: [...wafer.history, toStep(layer.madeBy, note ?? layer.note)],
  }
}

/** The surface layer, or null on a bare wafer. */
export const topLayer = (wafer: Wafer): FilmLayer | null =>
  wafer.stack.length > 0 ? wafer.stack[wafer.stack.length - 1] : null

/**
 * Thickness at a normalised radius, linearly interpolated.
 *
 * Stations do not share a radial grid and should not have to. A reflectometer
 * measures a spot at whatever radius the recipe puts it at; the deposition
 * solver samples wherever its mesh happens to fall. Interpolating here is what
 * lets those two disagree about grids and still agree about the wafer.
 */
export function thicknessAt(wafer: Wafer, layer: FilmLayer, xWanted: number): number {
  const x = wafer.x
  const n = x.length
  if (n === 0) return 0
  if (n === 1 || xWanted <= x[0]) return layer.thicknessNm[0]
  if (xWanted >= x[n - 1]) return layer.thicknessNm[n - 1]
  let i = 1
  while (i < n - 1 && x[i] < xWanted) i++
  const t = (xWanted - x[i - 1]) / (x[i] - x[i - 1])
  return layer.thicknessNm[i - 1] + t * (layer.thicknessNm[i] - layer.thicknessNm[i - 1])
}

export function meanThicknessNm(layer: FilmLayer): number {
  let sum = 0
  for (const v of layer.thicknessNm) sum += v
  return layer.thicknessNm.length > 0 ? sum / layer.thicknessNm.length : 0
}

/** Half-range non-uniformity of a layer [%], the way a fab quotes it. */
export function layerNonUniformityPct(layer: FilmLayer): number {
  if (layer.thicknessNm.length === 0) return 0
  let lo = Infinity
  let hi = -Infinity
  for (const v of layer.thicknessNm) {
    if (v < lo) lo = v
    if (v > hi) hi = v
  }
  const mean = meanThicknessNm(layer)
  return mean > 0 ? (100 * (hi - lo)) / (2 * mean) : 0
}

/**
 * Resample a profile onto the wafer's grid.
 *
 * A process module hands over whatever radial samples its solver produced;
 * this puts them on the wafer's grid so the next tool does not have to know
 * anything about the last one's mesh.
 */
export function resampleOntoWafer(
  wafer: Wafer,
  sourceX: ArrayLike<number>,
  sourceValues: ArrayLike<number>,
): Float64Array {
  const out = new Float64Array(wafer.x.length)
  const n = sourceX.length
  for (let k = 0; k < wafer.x.length; k++) {
    const xw = wafer.x[k]
    if (n === 0) {
      out[k] = 0
    } else if (n === 1 || xw <= sourceX[0]) {
      out[k] = sourceValues[0]
    } else if (xw >= sourceX[n - 1]) {
      out[k] = sourceValues[n - 1]
    } else {
      let i = 1
      while (i < n - 1 && sourceX[i] < xw) i++
      const t = (xw - sourceX[i - 1]) / (sourceX[i] - sourceX[i - 1])
      out[k] = sourceValues[i - 1] + t * (sourceValues[i] - sourceValues[i - 1])
    }
  }
  return out
}

// ------------------------------------------------------------------ carrier

/**
 * The carrier — one lot, shared by every tool for the length of a session.
 *
 * Named for the thing a technician actually walks between tools. It lives at
 * the session level for the same reason the house does: a wafer is a fact about
 * the fab, not about whichever panel happens to be open.
 *
 * Modules subscribe so that a wafer arriving at a tool is an event there,
 * the way a FOUP landing on a load port is an event.
 */
export class Carrier {
  private wafer: Wafer | null = null
  private readonly listeners = new Set<(w: Wafer | null) => void>()

  get current(): Wafer | null {
    return this.wafer
  }

  /** Put a wafer in the carrier, or empty it with null. */
  set(wafer: Wafer | null): void {
    this.wafer = wafer
    for (const listener of [...this.listeners]) listener(wafer)
  }

  subscribe(listener: (w: Wafer | null) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
}
